import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Upload, X, Clapperboard, Loader2, Film, User, Building2 } from 'lucide-react';

/**
 * 影片模板工廠 Demo（2026-08-23）—— 內部測試版
 * 模擬完整運作（通用行業）：照片輪播 + 標題/重點/次要/地點/文案 + 聯絡人資料
 * （姓名/電話/選填相片）→ video.jkdcoding.com 獨立 Worker → v9 定案版模板渲染。
 * 未填欄位自動用示範文案；Agent 相片未傳時用最後一張照片。
 */

const DEMO_API = 'https://video.jkdcoding.com';
const MAX_PHOTOS = 6;
const MIN_PHOTOS = 2;

interface PhotoItem {
  id: string;
  file: File;
  previewUrl: string;
}

interface FormState {
  title: string;
  price: string;
  area: string;
  address: string;
  description: string;
  agentName: string;
  agentPhone: string;
}

const EMPTY_FORM: FormState = {
  title: '', price: '', area: '', address: '', description: '', agentName: '', agentPhone: '',
};

type AspectRatio = 'square' | 'portrait';
type BgmStyle = 'modern' | 'luxury' | 'industrial' | 'cozy';

const inputCls =
  'w-full rounded-lg border border-gray-700 bg-jkd-black/80 px-3 py-2 text-sm text-white placeholder-gray-500 outline-none transition focus:border-jkd-gold/60';
const selectCls =
  'w-full rounded-lg border border-gray-700 bg-jkd-black/80 px-3 py-2 text-sm text-white outline-none transition focus:border-jkd-gold/60';

const VideoFactoryDemo: React.FC = () => {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [agentPhoto, setAgentPhoto] = useState<PhotoItem | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('square');
  const [bgmStyle, setBgmStyle] = useState<BgmStyle>('modern');
  const [videoStyle, setVideoStyle] = useState('modern');
  const [engine, setEngine] = useState('shotstack');
  const [includeAgentPhoto, setIncludeAgentPhoto] = useState(true);
  const [hashtags, setHashtags] = useState('');
  const [copied, setCopied] = useState(false);
  const [stage, setStage] = useState<'idle' | 'uploading' | 'rendering' | 'done' | 'error'>('idle');
  const [progressMsg, setProgressMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const agentInputRef = useRef<HTMLInputElement>(null);

  const setField = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const addFiles = useCallback((files: FileList | null) => {
    if (!files) return;
    const incoming = Array.from(files).filter((f) => f.type.startsWith('image/'));
    setPhotos((prev) => {
      const next = [...prev];
      for (const f of incoming) {
        if (next.length >= MAX_PHOTOS) break;
        next.push({
          id: `${f.name}_${f.lastModified}_${Math.random().toString(36).slice(2, 6)}`,
          file: f,
          previewUrl: URL.createObjectURL(f),
        });
      }
      return next.slice(0, MAX_PHOTOS);
    });
  }, []);

  const addAgentPhoto = useCallback((files: FileList | null) => {
    if (!files || !files[0] || !files[0].type.startsWith('image/')) return;
    const f = files[0];
    setAgentPhoto({
      id: `agent_${f.name}_${f.lastModified}`,
      file: f,
      previewUrl: URL.createObjectURL(f),
    });
  }, []);

  const removePhoto = (id: string) => {
    setPhotos((prev) => prev.filter((p) => p.id !== id));
    setStage('idle');
    setVideoUrl('');
  };

  const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

  const generate = useCallback(async () => {
    setErrorMsg('');
    setVideoUrl('');
    if (photos.length < MIN_PHOTOS) {
      setErrorMsg(`請選擇 ${MIN_PHOTOS}-${MAX_PHOTOS} 張照片`);
      setStage('error');
      return;
    }

    try {
      // 1) 上載照片（展示照 + 選填聯絡人相片）
      setStage('uploading');
      setProgressMsg('上載照片中…');
      const uploadOne = async (file: File): Promise<string> => {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${DEMO_API}/api/upload`, { method: 'POST', body: fd });
        const data = await res.json();
        if (!res.ok || !data.url) throw new Error(data.error || `上載失敗（HTTP ${res.status}）`);
        return data.url;
      };
      const useAgentPhoto = includeAgentPhoto && agentPhoto;
      const total = photos.length + (useAgentPhoto ? 1 : 0);
      let done = 0;
      const uploaded: string[] = [];
      for (const p of photos) {
        uploaded.push(await uploadOne(p.file));
        done++;
        setProgressMsg(`上載照片中… ${done}/${total}`);
      }
      let agentPhotoUrl: string | undefined;
      if (useAgentPhoto) {
        agentPhotoUrl = await uploadOne(agentPhoto!.file);
        setProgressMsg(`上載照片中… ${total}/${total}`);
      }

      // 2) 提交渲染（帶變量＋設定：比例／音樂）
      setStage('rendering');
      setProgressMsg('提交渲染…');
      const renderRes = await fetch(`${DEMO_API}/api/render`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          images: uploaded,
          agentPhoto: agentPhotoUrl,
          aspectRatio,
          bgmStyle,
          title: form.title.trim() || undefined,
          price: form.price.trim() || undefined,
          area: form.area.trim() || undefined,
          address: form.address.trim() || undefined,
          description: form.description.trim() || undefined,
          agentName: form.agentName.trim() || undefined,
          agentPhone: form.agentPhone.trim() || undefined,
        }),
      });
      const renderData = await renderRes.json();
      if (!renderRes.ok || !renderData.renderId) {
        throw new Error(renderData.error || `渲染提交失敗（HTTP ${renderRes.status}）`);
      }

      // 3) 輪詢（每 5 秒，最多 40 次 ≈ 200 秒）
      for (let i = 1; i <= 40; i++) {
        await sleep(5000);
        setProgressMsg(`渲染中…（${i * 5}s）`);
        const stRes = await fetch(`${DEMO_API}/api/status/${renderData.renderId}`);
        const st = await stRes.json();
        if (st.status === 'done' && st.videoUrl) {
          setVideoUrl(st.videoUrl);
          setStage('done');
          return;
        }
        if (st.status === 'failed') {
          throw new Error(st.error || '渲染失敗');
        }
      }
      throw new Error('渲染逾時（超過 200 秒），請稍後再試');
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
      setStage('error');
    }
  }, [photos, agentPhoto, form, includeAgentPhoto, aspectRatio, bgmStyle]);

  // 社群貼文文案（含自訂 Hashtag——只供複製貼文用，不會渲染進影片）
  const postCopy: string = useMemo(() => {
    const title = form.title.trim() || '我的最新作品';
    const desc = form.description.trim();
    const contact = [form.agentName.trim(), form.agentPhone.trim()].filter(Boolean).join('　');
    const tags = hashtags
      .split(/[\s,，]+/)
      .map((t) => t.replace(/^#*/, '#'))
      .filter((t) => t.length > 1)
      .join(' ');
    return [title, desc, contact, tags].filter(Boolean).join('\n');
  }, [form, hashtags]);

  const copyPost = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(postCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard 不可用時退回選取提示
      window.prompt('複製以下貼文文案：', postCopy);
    }
  }, [postCopy]);

  const busy = stage === 'uploading' || stage === 'rendering';

  return (
    <div className="mt-12 rounded-2xl border border-jkd-gold/30 bg-jkd-black/60 p-6 md:p-8">
      <div className="mb-6 flex items-center gap-3">
        <Clapperboard className="h-6 w-6 text-jkd-gold" />
        <div>
          <h3 className="text-xl font-bold text-white">親測：填資料、傳照片，生成你的示範影片</h3>
          <p className="text-sm text-gray-400">
            模擬完整流程（適用任何行業）——{MIN_PHOTOS}-{MAX_PHOTOS} 張照片＋文案與 Agent 資料（未填以示範文案代替），約 1-2 分鐘
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        {/* 展示照片 */}
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-gray-300">
            <Building2 className="h-4 w-4 text-jkd-gold" /> 展示照片（{MIN_PHOTOS}-{MAX_PHOTOS} 張，輪播展示）
          </div>
          <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-600 p-6 text-gray-400 transition hover:border-jkd-gold/60 hover:text-white">
            <Upload className="h-7 w-7" />
            <span className="text-sm">點擊選擇照片</span>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
            />
          </label>
          {photos.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-3">
              {photos.map((p) => (
                <div key={p.id} className="relative h-20 w-20 overflow-hidden rounded-lg border border-gray-700">
                  <img src={p.previewUrl} alt="" className="h-full w-full object-cover" />
                  <button
                    onClick={() => removePhoto(p.id)}
                    className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5 text-white hover:bg-red-600"
                    aria-label="移除"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 影片設定（風格／音樂／引擎／比例） */}
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-gray-300">
            <Clapperboard className="h-4 w-4 text-jkd-gold" /> 影片設定
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs text-gray-500">影片風格</span>
              <select className={selectCls} value={videoStyle} onChange={(e) => setVideoStyle(e.target.value)}>
                <option value="modern">現代簡約</option>
                <option value="luxury" disabled>奢華時尚（籌備中）</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-gray-500">背景音樂</span>
              <select className={selectCls} value={bgmStyle} onChange={(e) => setBgmStyle(e.target.value as BgmStyle)}>
                <option value="modern">現代簡約</option>
                <option value="luxury">奢華時尚</option>
                <option value="industrial">工業節奏</option>
                <option value="cozy">溫馨舒適</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-gray-500">影片引擎</span>
              <select className={selectCls} value={engine} onChange={(e) => setEngine(e.target.value)}>
                <option value="shotstack">Shotstack（現行，已驗證）</option>
                <option value="ice" disabled>阿里雲 ICE（籌備中）</option>
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs text-gray-500">影片比例</span>
              <select className={selectCls} value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value as AspectRatio)}>
                <option value="square">1:1 方形（1080×1080）</option>
                <option value="portrait">9:16 直式（1080×1920）</option>
              </select>
            </label>
          </div>
        </div>

        {/* 內容資料 */}
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-gray-300">
            <Building2 className="h-4 w-4 text-jkd-gold" /> 內容資料（選填）
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <input className={inputCls} placeholder="標題（品牌／品名／主題）" value={form.title} onChange={setField('title')} maxLength={40} />
            <input className={inputCls} placeholder="重點資訊（如：售價：$880萬）" value={form.price} onChange={setField('price')} maxLength={30} />
            <input className={inputCls} placeholder="次要資訊（如：面積／型號）" value={form.area} onChange={setField('area')} maxLength={30} />
            <input className={inputCls} placeholder="地點／網站／門市" value={form.address} onChange={setField('address')} maxLength={40} />
          </div>
          <textarea
            className={`${inputCls} mt-3 min-h-[90px]`}
            placeholder="介紹文案（完整保留顯示於第一張照片，不刪減）"
            value={form.description}
            onChange={setField('description')}
            maxLength={300}
          />
        </div>

        {/* Agent 資料 */}
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-gray-300">
            <User className="h-4 w-4 text-jkd-gold" /> 聯絡人資料（選填）
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <input className={inputCls} placeholder="聯絡人姓名" value={form.agentName} onChange={setField('agentName')} maxLength={30} />
            <input className={inputCls} placeholder="電話，如：(852) 9236 8966（自動去國碼）" value={form.agentPhone} onChange={setField('agentPhone')} maxLength={20} />
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={includeAgentPhoto}
                onChange={(e) => setIncludeAgentPhoto(e.target.checked)}
                className="h-4 w-4 accent-[#D4AF37]"
              />
              加入 Agent 照片
            </label>
            {includeAgentPhoto && (
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-400 transition hover:border-jkd-gold/60 hover:text-white">
                <Upload className="h-4 w-4" />
                {agentPhoto ? '更換 Agent 相片' : '上載聯絡人相片（預設用最後一張照片）'}
                <input
                  ref={agentInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => addAgentPhoto(e.target.files)}
                />
              </label>
            )}
            {includeAgentPhoto && agentPhoto && (
              <div className="relative h-14 w-14 overflow-hidden rounded-full border border-jkd-gold/50">
                <img src={agentPhoto.previewUrl} alt="" className="h-full w-full object-cover" />
                <button
                  onClick={() => setAgentPhoto(null)}
                  className="absolute right-0 top-0 rounded-full bg-black/70 p-0.5 text-white hover:bg-red-600"
                  aria-label="移除"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* 自訂 Hashtag（社群貼文用，不會渲染進影片） */}
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-bold text-gray-300">
            <User className="h-4 w-4 text-jkd-gold" /> 自訂 Hashtag
          </div>
          <input
            className={inputCls}
            placeholder="例如：#jkdcoding #影片模板 #宣傳影片（用於社群貼文，不會顯示在影片）"
            value={hashtags}
            onChange={(e) => setHashtags(e.target.value)}
            maxLength={200}
          />
        </div>

        <button
          onClick={generate}
          disabled={busy || photos.length < MIN_PHOTOS}
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-jkd-gold px-6 py-3 font-bold text-jkd-black transition disabled:cursor-not-allowed disabled:opacity-40"
        >
          {busy ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              {progressMsg}
            </>
          ) : (
            <>
              <Film className="h-5 w-5" />
              生成示範影片
            </>
          )}
        </button>

        {stage === 'error' && (
          <div className="rounded-lg border border-red-500/40 bg-red-950/40 p-4 text-sm text-red-300">
            {errorMsg}
          </div>
        )}

        {videoUrl && (
          <div className="overflow-hidden rounded-xl border border-jkd-gold/40">
            <video src={videoUrl} controls playsInline className="w-full" />
            <a
              href={videoUrl}
              target="_blank"
              rel="noreferrer"
              className="block bg-jkd-black/80 p-3 text-center text-sm text-jkd-gold underline"
            >
              下載 / 新視窗播放
            </a>
          </div>
        )}

        {videoUrl && (
          <div className="rounded-xl border border-gray-700 bg-jkd-black/40 p-4">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-bold text-gray-300">社群貼文文案（含你的 Hashtag）</span>
              <button
                onClick={copyPost}
                className="rounded-lg border border-jkd-gold/50 px-3 py-1 text-xs text-jkd-gold transition hover:bg-jkd-gold/10"
              >
                {copied ? '已複製 ✓' : '複製文案'}
              </button>
            </div>
            <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-lg bg-black/50 p-3 text-sm text-gray-300">{postCopy}</pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoFactoryDemo;
